import type { FoundryV2SimulationEngineRecord } from "../api/foundryV2Client";

function statusTone(status: "ready" | "watch" | "blocked"): string {
  if (status === "ready") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "watch") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-red-200 bg-red-50 text-red-700";
}

type Props = {
  simulationEngine: FoundryV2SimulationEngineRecord | null;
  isLoading: boolean;
  error: string | null;
};

export function FoundrySimulationEnginePanel({
  simulationEngine,
  isLoading,
  error,
}: Props): JSX.Element {
  const report = simulationEngine?.report ?? null;
  const preflight = simulationEngine?.preflight ?? null;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-simulation-engine-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
            Simulation Engine v1
          </div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">
            Pre-build simulation of failures, dependency pressure, debt, and launch confidence before Jarvis starts
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            This surface forces Foundry to show the likely failure modes instead of hiding them behind a green checklist.
            Promotion is blocked when the simulated launch path is not materially closed.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !simulationEngine ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading simulation engine...
        </div>
      ) : null}

      {report ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.1fr_0.9fr_0.9fr_0.9fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(report.summary.status)}`}>
                  {report.summary.status}
                </span>
                <span className="text-xs text-slate-500">{report.summary.launchConfidence}% confidence</span>
              </div>
              <div className="mt-3 text-sm text-slate-700">{report.summary.headline}</div>
              <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {report.summary.blockingScenarioCount} blocking scenario(s)
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {report.summary.watchScenarioCount} watch scenario(s)
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {report.summary.dependencyCount} dependency pressure(s)
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {preflight?.verdict === "launch" ? "Jarvis can launch" : "Jarvis should hold"}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Founder view</div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {report.readableViews.founder.map((item) => (
                  <li key={item} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">{item}</li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Tactical view</div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {report.readableViews.tactical.map((item) => (
                  <li key={item} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">{item}</li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifacts</div>
              <div className="mt-3 space-y-2 text-xs">
                <a className="block font-medium text-blue-700 underline" href={simulationEngine?.artifactUrls.simulationReport ?? "#"}>
                  simulation_report.v1.json
                </a>
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Scenario map</div>
              <div className="mt-3 space-y-3">
                {report.scenarios.map((scenario) => (
                  <div key={scenario.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{scenario.title}</div>
                        <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          {scenario.category}
                        </div>
                      </div>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(scenario.severity === "blocking" ? "blocked" : scenario.severity === "watch" ? "watch" : "ready")}`}>
                        {scenario.severity}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-slate-600">{scenario.reason}</div>
                    <div className="mt-2 text-xs text-slate-500">Impact: {scenario.impact}</div>
                    <div className="mt-2 text-xs font-medium text-slate-700">Next action: {scenario.nextAction}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Dependency pressures</div>
                <div className="mt-3 space-y-3 text-xs text-slate-600">
                  {report.dependencyPressures.map((pressure) => (
                    <div key={pressure.id} className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold text-slate-800">{pressure.label}</div>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(pressure.status === "blocking" ? "blocked" : pressure.status === "watch" ? "watch" : "ready")}`}>
                          {pressure.status}
                        </span>
                      </div>
                      <div className="mt-2">{pressure.rationale}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Debt hotspots</div>
                <div className="mt-3 space-y-3 text-xs text-slate-600">
                  {report.debtHotspots.map((hotspot) => (
                    <div key={hotspot.id} className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold text-slate-800">{hotspot.label}</div>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(hotspot.severity === "blocking" ? "blocked" : hotspot.severity === "watch" ? "watch" : "ready")}`}>
                          {hotspot.severity}
                        </span>
                      </div>
                      <div className="mt-2">{hotspot.reason}</div>
                      <div className="mt-2 text-[11px] text-slate-500">Owner lane: {hotspot.ownerLane}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cost drivers</div>
                <div className="mt-3 space-y-3 text-xs text-slate-600">
                  {report.costDrivers.map((driver) => (
                    <div key={driver.id} className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold text-slate-800">{driver.label}</div>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(driver.status === "blocking" ? "blocked" : driver.status === "watch" ? "watch" : "ready")}`}>
                          {driver.status}
                        </span>
                      </div>
                      <div className="mt-2">{driver.rationale}</div>
                      <div className="mt-2 text-[11px] text-slate-500">Mitigation: {driver.mitigation}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
